<template>
  <div class="settings-block">
    <div class="settings-block__title">
      <div class="settings-block__title-text">
        {{ t('Reporting') }}
      </div>
      <div class="settings-block__title-buttons">
        <ui-button
          v-show="!edit"
          :text="t('Edit')"
          icon="fas fa-edit"
          button-style="outline-accent"
          @click="edit = true"
        />
        <ui-button
          v-show="edit"
          :text="t('Cancel')"
          icon="fas fa-times"
          :disabled="putLoading"
          @click="cancel"
        />
        <ui-button
          v-show="edit"
          :text="t('Save')"
          icon="fas fa-check"
          button-style="accent"
          :loading="putLoading"
          @click="submit"
        />
      </div>
    </div>
    <div class="settings-block__content">
      <ui-select
        name="timezone"
        item-text="name"
        item-value="zone"
        v-model="form.defaultTimezone"
        :return-object="false"
        :items="store.timezoneList"
        :placeholder="placeholderTimezone && t('Report timezone')"
        :label="t('Report timezone')"
        :disabled="!edit"
      />
      <div>
        <div class="currencies">
          <div class="font-weight-bold">{{ t('Visible Currencies') }}</div>
          <div class="exchange-rates" @click="openExchangeRatesDialog">
            <ui-icon
              :color="$colors.blue"
              class="mr-4 mt-4"
              icon="fas fa-info-circle"
            ></ui-icon>
            <div class="rates-label">{{ t('Exchange Rates') }}</div>
          </div>
        </div>
        <ui-select
          class="mt-4"
          menuContentClass="ui-select-visible-currencies"
          v-model="form.preferredCurrency"
          :items="currencyListOrderedItems"
          :autocomplete="false"
          item-text="code"
          item-value="code"
          :hide-details="2"
          :disabled="!edit"
          :return-object="false"
          multiple
          chips
          attach
          multi-line
        >
          <template #item="{ item, props: { onClick } }">
            <v-list-item
              v-bind="item"
              :title="null"
              @click="onClick"
              :disabled="item.raw.props.disabled"
            >
              <div class="reporting-currency-item__row">
                <span class="currency-picker__currency-text">
                  {{ item.raw.name }}
                </span>
                <div class="currency-picker__currency-icon">
                  {{ item.raw.sign }} {{ item.raw.code }}
                </div>
              </div>
            </v-list-item>
          </template>
        </ui-select>
      </div>

      <div class="row-2-columns-wrp">
        <div class="row-2-columns-left">
          <ui-radio-group
            v-model="form.defaultHomepage"
            :disabled="!edit"
            :label="t('Default Homepage')"
          >
            <ui-radio :disabled="!edit" :label="t('Dashboard')" value="dashboard" />
            <ui-radio :disabled="!edit" :label="t('Campaigns')" value="campaigns" />
          </ui-radio-group>
          <ui-select
            v-model="form.dashboardDefaultGroup"
            name="dashboard_grouping"
            item-text="text"
            item-value="value"
            :return-object="false"
            :items="default_group_list"
            :placeholder="placeholderGrouping && t('Dashboard Grouping')"
            :label="t('Dashboard Grouping')"
            :disabled="!edit"
            :autocomplete="false"
            attach
          />
          <div v-if="!store.user.isMember">
            <ui-label :text="t('Show Country Name In')" />
            <ui-checkbox
              :label="t('Campaigns')"
              v-model="form.showCountry"
              :disabled="!edit"
            />
            <ui-checkbox
              :label="t('Flows')"
              v-model="form.reportFlowsName"
              :disabled="!edit"
              true-value="full"
              false-value="name-only"
            />
            <ui-checkbox
              :label="t('Landings')"
              v-model="form.reportLandingsName"
              :disabled="!edit"
              true-value="full"
              false-value="name-only"
            />
            <ui-checkbox
              :label="t('Offers')"
              v-model="form.reportOffersName"
              :disabled="!edit"
              true-value="full"
              false-value="name-only"
            />
          </div>
        </div>
        <div class="row-2-columns-right">
          <ui-radio-group v-model="form.postbackDate" :disabled="!edit">
            <template #label>
              <span class="label font-weight-bold">
                {{ t('Conversion time in reports') }}
                <ui-tooltip-icon
                  preset="help"
                  class="mb-2"
                  :text="
                    t(
                      'Visit timestamp - conversions will be counted on visit date. Postback timestamp - conversions will be counted on postback date. This change will affect new conversions only.',
                    )
                  "
                />
              </span>
            </template>
            <ui-radio :disabled="!edit" :label="t('Visit Timestamp')" value="visit" />
            <ui-radio
              :disabled="!edit"
              :label="t('Postback Timestamp')"
              value="postback"
            />
          </ui-radio-group>
          <ui-select
            v-model="form.dashboardDefaultOrderDir"
            name="dashboard_sorting"
            item-text="text"
            item-value="value"
            :return-object="false"
            :items="conversion_time_list"
            :placeholder="placeholderSorting && t('Dashboard Sorting')"
            :label="t('Dashboard Sorting')"
            :disabled="!edit"
            :autocomplete="false"
            attach
          />
          <div class="flex-wrap__checkbox" xs6>
            <template v-if="!store.user.isMember">
              <ui-label :text="t('Show Traffic Source Name In')" />
              <ui-checkbox
                :label="t('Campaigns')"
                v-model="form.showTrafficSource"
                :disabled="!edit"
              />
            </template>

            <ui-label :text="t('Columns settings')" />

            <ui-checkbox
              v-model="form.independentColumnSettingsEnabled"
              :disabled="!edit"
              :label="t('Independent Columns Settings')"
              :help-tooltip="
                t(
                  'Mark the checkbox if you wish to set up the different order, number or width of columns for each report section.<br>If the option is not selected, the same order, number and width of the columns will be applied to your reports.',
                )
              "
            />

            <template v-if="!store.user.isMember">
              <ui-checkbox
                v-model="form.reportTotalLabelEnabled"
                :disabled="!edit"
                :label="t('Show Value Type in Total Line')"
                :help-tooltip="
                  t(
                    'Unmark the checkbox if you wish to hide the Def, Sum, Avg in the Totals line.',
                  )
                "
              />
            </template>
          </div>
        </div>
      </div>

      <div class="d-flex flex-wrap__select reporting-form__wrap-select-custom">
        <div class="flex-select pr-16 reporting-form__search-select row-2-columns-left">
          <ui-select
            v-model="form.reportSearchSetting"
            :items="reportSearchSettingItems"
            :placeholder="t('Search Settings')"
            :label="t('Search Settings')"
            :disabled="!edit"
            :autocomplete="false"
            :hide-details="2"
            :return-object="false"
          ></ui-select>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { pick } from 'lodash';
import { useColors, useI18n, useMixpanel, useCurrencyList } from '@/composables';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';
import { putUserApiRequest } from '@/services/api/user-api';
import type { Timezone, User } from '@/services/models';
import { sortByPickerData } from '@/views/dashboard/pickers/base/utils';

const { t } = useI18n();
const $colors = useColors();
const { trackMixpanel } = useMixpanel();
const store = useStore();
const { currencyListOrdered } = useCurrencyList();

const edit = ref(false);
const putLoading = ref(false);
const placeholderTimezone = ref(true);
const placeholderGrouping = ref(true);
const placeholderSorting = ref(true);

const default_group_list = ref<{ value: string; text: string }[]>([]);
const conversion_time_list = ref<{ value: string; text: string }[]>([]);
const reportSearchSettingItems = ref<{ value: string; text: string }[]>([]);

const REPORTING_PUT_KEYS = [
  'defaultTimezone',
  'defaultHomepage',
  'postbackDate',
  'dashboardDefaultGroup',
  'dashboardDefaultOrderDir',
  'reportCampaignsName',
  'reportOffersName',
  'reportLandingsName',
  'reportFlowsName',
  'preferredCurrency',
  'independentColumnSettingsEnabled',
  'reportTotalLabelEnabled',
  'reportSearchSetting',
] as const;

type ReportingFormState = Partial<Pick<User, (typeof REPORTING_PUT_KEYS)[number]>> & {
  showCountry: boolean;
  showTrafficSource: boolean;
};

const form = reactive<ReportingFormState>({
  showCountry: false,
  showTrafficSource: false,
});

const currencyListOrderedItems = computed(() => {
  const currencyId = (store.user as User).currencyId;
  return currencyListOrdered.value.map((item) => {
    const next = { ...item, props: { ...(item.props || {}) } };
    if (next.code === currencyId) {
      next.props.disabled = true;
    }
    return next;
  });
});

const fillForm = (source: User) => {
  form.showCountry =
    source.reportCampaignsName === 'full' ||
    source.reportCampaignsName === 'country-only';
  form.showTrafficSource =
    source.reportCampaignsName === 'full' ||
    source.reportCampaignsName === 'traffic-source-only';

  Object.assign(form, pick(source, [...REPORTING_PUT_KEYS]));

  if (store.timezoneList.length) {
    placeholderTimezone.value = false;
  }
  if (default_group_list.value.length) {
    placeholderGrouping.value = false;
  }
  if (conversion_time_list.value.length) {
    placeholderSorting.value = false;
  }
};

const openExchangeRatesDialog = () => {
  $emitter.emit('open-dialog', { name: 'ExchangeRatesDialog' });
};

const trackAnalytics = (event: string) => {
  const { independentColumnSettingsEnabled } = form;
  trackMixpanel(event, {
    independent_columns_settings: independentColumnSettingsEnabled,
  });
};

const cancel = () => {
  edit.value = false;
  fillForm(store.user as User);
};

const submit = async () => {
  form.reportCampaignsName = 'full';

  if (form.showCountry && !form.showTrafficSource) {
    form.reportCampaignsName = 'country-only';
  }

  if (!form.showCountry && form.showTrafficSource) {
    form.reportCampaignsName = 'traffic-source-only';
  }

  if (!form.showCountry && !form.showTrafficSource) {
    form.reportCampaignsName = 'campaign-only';
  }

  putLoading.value = true;
  try {
    const payload = pick(form, [...REPORTING_PUT_KEYS]) as Partial<User>;
    const res = await putUserApiRequest(payload);
    trackAnalytics('settings_reporting');

    store.$patch(state => {
      state.user = { ...state.user, ...res };
    });
    edit.value = false;
    $emitter.emit('toast-success', t('Changes have been successfully saved'));

    const sorting = sortByPickerData().find(
      item => item.userValue === res.dashboardDefaultGroup,
    );
    if (sorting) {
      store.dashboardSortBy = { title: sorting.title, value: sorting.pickerValue };
    }
    store.dashboardOrderDir = res.dashboardDefaultOrderDir || 'asc';
  } finally {
    putLoading.value = false;
  }
};

watch(
  () => store.user,
  () => {
    fillForm(store.user as User);
  },
  { deep: true },
);

onMounted(() => {
  fillForm(store.user as User);

  default_group_list.value = [
    { value: 'revenue', text: t('Revenue') },
    { value: 'cost', text: t('Cost') },
    { value: 'profit', text: t('Profit') },
    { value: 'visits', text: t('Visits') },
    { value: 'clicks', text: t('Clicks') },
    { value: 'conversions', text: t('Conversions') },
  ];

  reportSearchSettingItems.value = [
    { value: 'allTabs', text: t('Save search across all report tabs') },
    { value: 'perTab', text: t('Apply search per tab') },
  ];

  conversion_time_list.value = [
    { value: 'asc', text: t('Ascending') },
    { value: 'desc', text: t('Descending') },
  ];

  if (store.timezoneList.length) {
    placeholderTimezone.value = false;
  }
  if (default_group_list.value.length) {
    placeholderGrouping.value = false;
  }
  if (conversion_time_list.value.length) {
    placeholderSorting.value = false;
  }
});
</script>

<style lang="scss" scoped>
.currencies {
  display: flex;
  justify-content: space-between;

  .exchange-rates {
    display: flex;
    align-items: flex-start;
    cursor: pointer;
  }
}

.rates-label {
  color: var(--color-blue);
}

.icon-tooltip {
  pointer-events: all;
}
</style>

<style lang="scss">
.ui-select-visible-currencies {
  .v-list-item {
    width: 100%;
  }

  .v-list-item__content {
    width: 100%;
    min-width: 0 !important;
    box-sizing: border-box;
  }

  .reporting-currency-item__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 8px;
    min-width: 0;
  }

  .reporting-currency-item__row .currency-picker__currency-text {
    flex: 1;
    min-width: 0;
  }
}
</style>
